import React, { Fragment, ReactNode } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { AsyncIndicator } from '@zapier/common-components';
import { Price, useCurrentAccountId } from '@zapier/design-system-revenue';

export const teamsPricingAccountQuery = gql`
  query teamsPricingAccountQuery($accountId: Int!) {
    account(id: $accountId) {
      id
      plan: planV2 {
        id
        interval
      }
    }
  }
`;

export const teamsPricingQuery = gql`
  query teamsPricingQuery($accountId: Int) {
    currentUser: currentUserV2 {
      id
    }

    planStacks: pricingPageV3Plans(accountId: $accountId) {
      plans {
        amount
        id
        interval
        name
      }
      planType
    }
  }
`;

export const _useData = () => {
  /**
   * TL;DR:
   *
   * Determine if the user is logged in. If so, grab their current plan
   * interval. If not, default to yearly billing.
   */
  const { accountId } = useCurrentAccountId();

  const { data, loading: isLoading, error } = useQuery(teamsPricingQuery, {
    variables: {
      accountId,
    },
  });

  const {
    data: accountData,
    loading: isAccountDataLoading,
    error: accountError,
  } = useQuery(teamsPricingAccountQuery, {
    skip: !data?.currentUser || !accountId,
    variables: {
      accountId,
    },
  });

  const currentPlanInterval = accountData?.account?.plan?.interval;

  const teamPlanStack = data?.planStacks?.find(
    planStack => planStack.planType === 'Team'
  );

  const lowestTeamPlan = teamPlanStack?.plans?.find(
    plan => plan.interval === (currentPlanInterval ?? 'year') // will default to year when user is logged out
  );

  const isAnnual = Boolean(lowestTeamPlan?.interval === 'year');

  return {
    error:
      error || accountError
        ? 'There was an error fetching pricing information. Please try again.'
        : null,
    isAnnual,
    isLoading: isLoading || isAccountDataLoading,
    monthlyAmount: isAnnual
      ? lowestTeamPlan?.amount / 12
      : lowestTeamPlan?.amount,
  };
};

type TeamsPricingProps = {
  children: ({ price: string, isAnnual: boolean }) => ReactNode;
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

const TeamsPricing = ({ children, useData = _useData }: TeamsPricingProps) => {
  const { isAnnual, isLoading, monthlyAmount } = useData();

  if (isLoading) {
    return <AsyncIndicator />;
  }

  return <Fragment>{children({ price: monthlyAmount, isAnnual })}</Fragment>;
};

TeamsPricing.defaultProps = {
  children: ({ price, isAnnual }) => (
    <Fragment>
      <Price>{price}</Price>
      {`/mo ${isAnnual ? '(paid annually)' : ''}`.trim()}
    </Fragment>
  ),
};

export default TeamsPricing;
